import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment } from 'react'
import { GroupExclamationIcon } from 'components/Icons/GroupExclamation'
import {
    ButtonAction,
    ButtonsContainer,
    ContentContainer,
    dialogPanel,
    dialogTitle
} from './styles'

// Spinner simples para loading
const Spinner = () => (
    <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
)

interface GroupRemoveNotificationsModalProps {
    isOpen: boolean
    handleCloseModal: () => void
    handleConfirmRemoval: () => void
    treatmentStatus: 'started' | 'not_started'
    selectedCount: number
    isLoading?: boolean
}

const GroupRemoveNotificationsModal: React.FC<GroupRemoveNotificationsModalProps> = ({
    isOpen,
    handleCloseModal,
    handleConfirmRemoval,
    treatmentStatus,
    selectedCount,
    isLoading = false
}) => {
    const isTreatmentStarted = treatmentStatus === 'started'
    
    // Altura dinâmica baseada no status do tratamento
    const modalHeight = isTreatmentStarted ? '441px' : '417px'
    
    const getSubtitle = () => {
        return isTreatmentStarted ? 'Tratamento iniciado' : 'Tratamento não iniciado'
    }
    
    const getBodyText = () => {
        if (isTreatmentStarted) {
            return [
                'Há dados de tratamento já registrados.As notificações removidas \nserão desvinculadas do grupo, porém continuarão com o \ntratamento em andamento, e em seu nome.\nNão será possível desfazer essa ação.',
                'Tem certeza que deseja remover as notificações selecionadas do \ntratamento em grupo?'
            ]
        } else {
            return [
                'Não há dados de tratamento registrados, por isso as notificações \nremovidas serão desvinculadas do grupo, e voltarão para o status \naguardando tratamento.Não será possível desfazer essa ação.',
                'Tem certeza que deseja remover as notificações selecionadas do \ntratamento em grupo?'
            ]
        }
    }

    const renderTextWithLineBreaks = (text: string) => {
        return text.split('\n').map((line, index) => (
            <React.Fragment key={index}>
                {line}
                {index < text.split('\n').length - 1 && <br />}
            </React.Fragment>
        ))
    }

    return (
        <>
            <Transition
                appear
                show={isOpen}
                as={Fragment}
                data-testid="test-group-remove-notifications-modal"
            >
                <Dialog
                    as="div"
                    className="relative z-10"
                    onClose={() => { if (!isLoading) handleCloseModal() }}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className={dialogPanel(modalHeight)}>
                                    <Dialog.Title
                                        as="h3"
                                        className={dialogTitle}
                                    >
                                        <div className="flex flex-col items-center w-full mb-4">
                                            <GroupExclamationIcon />
                                            <p className="text-[#F0B323] text-[20px] font-semibold mt-4">
                                                Remover notificações do grupo
                                            </p>
                                            <p className="text-darkGray text-[14px] font-semibold">
                                                {getSubtitle()}
                                            </p>
                                        </div>
                                    </Dialog.Title>
                                    
                                    <ContentContainer>
                                        <div className="text-[16px] text-gray-700 mb-2 space-y-4">
                                            {getBodyText().map((text, index) => (
                                                <p key={index} className="text-center">
                                                    {renderTextWithLineBreaks(text)}
                                                </p>
                                            ))}
                                        </div>
                                        
                                        <ButtonsContainer>
                                            <ButtonAction
                                                onClick={handleCloseModal}
                                                type="button"
                                                disabled={isLoading}
                                                className="text-blueTheme text-sm border border-blueTheme disabled:opacity-50"
                                            >
                                                Cancelar remoção
                                            </ButtonAction>
                                            <ButtonAction
                                                onClick={() => {
                                                    handleConfirmRemoval()
                                                }}
                                                disabled={isLoading}
                                                className={`bg-blueTheme text-white text-sm font-semibold flex items-center justify-center gap-2 ${
                                                    isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-90'
                                                }`}
                                            >
                                                {isLoading && <Spinner />}
                                                {isLoading ? 'Removendo...' : 'Sim, remover'}
                                            </ButtonAction>
                                        </ButtonsContainer>
                                    </ContentContainer>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    )
}

export { GroupRemoveNotificationsModal } 